import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Printer, Download, ArrowLeft, Pencil } from "lucide-react";

/**
 * InvoicePrint — thin shell around the backend-generated PDF.
 * The on-screen preview is the exact same PDF that Download PDF returns
 * (Excel template → LibreOffice → PDF), so what you see is what you get.
 */
export default function InvoicePrint() {
  const { invoice_id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null); // { message, needsTemplate }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef(null);

  const loadPreview = React.useCallback(async () => {
    setPdfError(null);
    setPdfUrl(null);
    setPreviewLoading(true);
    try {
      const res = await api.get(`/api/invoices/${invoice_id}/pdf-preview`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
      setPdfUrl(url);
    } catch (err) {
      let msg = "Preview PDF is unavailable.";
      const status = err?.response?.status;
      try {
        if (err?.response?.data) {
          const txt = err.response.data instanceof Blob ? await err.response.data.text() : JSON.stringify(err.response.data);
          const parsed = JSON.parse(txt);
          if (parsed?.detail) msg = parsed.detail;
        }
      } catch { /* non-JSON error body — fall through to generic message */ }
      // 412 = template missing (offer Settings link). Anything else = transient render failure (offer Retry).
      setPdfError({ message: msg, needsTemplate: status === 412 });
    } finally {
      setPreviewLoading(false);
    }
  }, [invoice_id]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/api/invoices/${invoice_id}`).then((res) => {
      if (!cancelled) setInv(res.data.invoice);
    });
    loadPreview();
    return () => { cancelled = true; };
  }, [invoice_id, loadPreview]);

  const downloadPdf = async () => {
    if (!inv) return;
    setDownloading(true);
    try {
      const res = await api.get(`/api/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${inv.invoice_number}.pdf`.replace(/\//g, "-");
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("PDF download failed", err);
      let msg = "Could not generate the PDF — please try again.";
      try {
        if (err?.response?.data) {
          const txt = err.response.data instanceof Blob ? await err.response.data.text() : JSON.stringify(err.response.data);
          const parsed = JSON.parse(txt);
          if (parsed?.detail) msg = parsed.detail;
        }
      } catch { /* non-JSON error body — use generic message */ }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  if (!inv) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-100 py-6" data-testid="invoice-print-page">
      <div className="max-w-4xl mx-auto mb-4 flex justify-between items-center print:hidden">
        <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/invoices?edit=${inv.id}`)} data-testid="edit-invoice-from-print">
            <Pencil className="w-4 h-4 mr-2" />Edit
          </Button>
          <Button variant="outline" onClick={() => pdfUrl && window.open(pdfUrl, "_blank")?.print()} data-testid="print-invoice-btn">
            <Printer className="w-4 h-4 mr-2" />Print
          </Button>
          <Button className="bg-brand hover:bg-brand-hover" onClick={downloadPdf} disabled={downloading} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />{downloading ? "Preparing…" : "Download PDF"}
          </Button>
        </div>
      </div>
      <div ref={printRef} className="max-w-4xl mx-auto shadow-lg print:shadow-none bg-white" style={{ minHeight: 1050 }}>
        {pdfError ? (
          <div className="p-8 text-center" data-testid="invoice-preview-error">
            <div className="text-slate-700 font-heading font-medium mb-2">
              {pdfError.needsTemplate ? "Template not uploaded" : "Preview unavailable"}
            </div>
            <div className="text-sm text-slate-500 mb-4 max-w-xl mx-auto">{pdfError.message}</div>
            <div className="flex gap-2 justify-center flex-wrap">
              {pdfError.needsTemplate ? (
                <Button variant="outline" onClick={() => navigate("/settings")} data-testid="goto-settings-templates">
                  Open Settings → Invoice Templates
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={loadPreview}
                    disabled={previewLoading}
                    data-testid="retry-preview-btn"
                  >
                    {previewLoading ? "Retrying…" : "Retry preview"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await api.get(`/api/invoices/${inv.id}/xlsx`, { responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `Invoice-${inv.invoice_number}.xlsx`.replace(/\//g, "-");
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch (e) {
                        toast.error("Could not download XLSX");
                      }
                    }}
                    data-testid="download-xlsx-fallback-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Download XLSX
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : pdfUrl ? (
          <img
            src={pdfUrl}
            alt="Invoice preview"
            data-testid="invoice-pdf-iframe"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        ) : (
          <div className="p-16 text-center text-slate-400">Rendering invoice PDF…</div>
        )}
      </div>
    </div>
  );
}
